import { apiClient } from './api-client'

export interface User {
  id: string
  firstName: string
  lastName: string
  email: string
  role: string
  isActive: boolean
  emailVerified?: boolean
  oabNumber?: string
  oabState?: string
  lastLogin?: string
  createdAt: string
  updatedAt: string
}

export interface CreateUserData {
  firstName: string
  lastName: string
  email: string
  password: string
  role: string
  isActive: boolean
}

export interface UpdateUserData {
  firstName: string
  lastName: string
  email: string
  password?: string
  role: string
  isActive: boolean
  oabNumber?: string
  oabState?: string
}

export interface UsersListResponse {
  users: User[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface UsersFilters {
  page?: number
  limit?: number
  search?: string
  role?: string
  isActive?: boolean
}

class UserService {
  private readonly baseUrl = '/api/auth/users'

  async getUsers(filters?: UsersFilters): Promise<User[]> {
    const params = new URLSearchParams()
    
    if (filters?.page) params.append('page', filters.page.toString())
    if (filters?.limit) params.append('limit', filters.limit.toString())
    if (filters?.search) params.append('search', filters.search)
    if (filters?.role) params.append('role', filters.role)
    if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString())

    const response = await apiClient.get(`${this.baseUrl}${params.toString() ? `?${params.toString()}` : ''}`)
    return response.data.data
  }

  async getUserById(id: string): Promise<User> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`)
    return response.data
  }

  async createUser(userData: CreateUserData): Promise<User> {
    const response = await apiClient.post(this.baseUrl, userData)
    return response.data
  }

  async updateUser(id: string, userData: UpdateUserData): Promise<User> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, userData)
    return response.data
  }

  async deleteUser(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`)
  }

  async toggleUserStatus(id: string): Promise<User> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/toggle-status`)
    return response.data
  }

  async resetUserPassword(id: string, newPassword: string): Promise<void> {
    await apiClient.patch(`${this.baseUrl}/${id}/reset-password`, { password: newPassword })
  }

  // Mock data for development/testing
  async getMockUsers(): Promise<User[]> {
    return [
      {
        id: '1',
        firstName: 'Admin',
        lastName: 'Sistema',
        email: 'admin@lawtech.pro',
        role: 'SUPER_ADMIN',
        isActive: true,
        lastLogin: '2024-01-15T10:30:00.000Z',
        createdAt: '2024-01-01T00:00:00.000Z',
        updatedAt: '2024-01-15T10:30:00.000Z'
      },
      {
        id: '2',
        firstName: 'Dr. João',
        lastName: 'Silva',
        email: 'joao.silva@lawtech.pro',
        role: 'LAWYER',
        isActive: true,
        lastLogin: '2024-01-14T16:45:00.000Z',
        createdAt: '2024-01-01T00:00:00.000Z',
        updatedAt: '2024-01-14T16:45:00.000Z'
      },
      {
        id: '3',
        firstName: 'Maria',
        lastName: 'Santos',
        email: 'maria.santos@lawtech.pro',
        role: 'ASSISTANT',
        isActive: true,
        lastLogin: '2024-01-13T09:15:00.000Z',
        createdAt: '2024-01-01T00:00:00.000Z',
        updatedAt: '2024-01-13T09:15:00.000Z'
      },
      {
        id: '4',
        firstName: 'Carlos',
        lastName: 'Oliveira',
        email: 'carlos@cliente.com',
        role: 'CLIENT',
        isActive: false,
        createdAt: '2024-01-01T00:00:00.000Z',
        updatedAt: '2024-01-01T00:00:00.000Z'
      }
    ]
  }
}

export const userService = new UserService()